import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";

// Define the form schema using zod
const formSchema = z.object({
  label: z.string().min(1, { message: "Label is required" }),
});

type FormValues = z.infer<typeof formSchema>;

export interface AddCustomLabelDialogProps {
  onAdd?: (label: string) => void;
  onCancel?: () => void;
}

export const AddCustomLabelDialog = ({
  onAdd,
  onCancel,
}: AddCustomLabelDialogProps) => {
  const [open, setOpen] = useState(false);

  // Initialize form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: "",
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (onAdd) {
      onAdd(values.label);
    }
    form.reset();
    setOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    form.reset();
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="sm"
          className="ml-2"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Label
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[350px] p-0">
        <div className="flex h-[60px] w-full items-center justify-center rounded-sm bg-primary">
          <DialogTitle className="text-white">
            {form.watch("label") || "New Label"}
          </DialogTitle>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="labelName">Label Name</FormLabel>
                    <FormControl>
                      <Input
                        id="labelName"
                        type="text"
                        placeholder="Enter label name"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col items-center space-y-3 px-5 py-4">
              <Button
                type="submit"
                variant="accent"
                className="h-[40px] w-full"
              >
                Add Label
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full text-sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
